/**
 * Display hidden column on button click, hide the clicked button, and scroll the revealed column into view.
 * Everything is scoped to the toggle's own feature-table instance, so any number of tables can coexist
 * on a single page (e.g. the UI Kit story renders several variations at once).
 * @param {HTMLElement} clickedButton
 * @returns {void}
 */
function displayColumn(clickedButton) {
    /** @type {HTMLElement|null} */
    const tableRoot = clickedButton.closest('.js-membershipFeatureTable');
    if (tableRoot === null) {
        return;
    }

    /**
     * Column ID to show
     * @type {string}
     */
    const columnId = clickedButton.dataset.columnId;

    /** @type {HTMLTableElement|null} */
    const table = tableRoot.querySelector('[data-comparison-table]');
    if (table === null) {
        return;
    }

    // Target the column by its `data-column` marker rather than its position, so the reveal keeps
    // working regardless of how many benefit rows or columns the table renders.
    const columnCells = table.querySelectorAll(`[data-column="${columnId}"]`);
    if (columnCells.length === 0) {
        return;
    }

    clickedButton.classList.add('hidden');

    columnCells.forEach((columnCell) => {
        columnCell.setAttribute('aria-hidden', 'false');
    });

    tableRoot.querySelector('.js-membershipFeatureTableTestimonials')?.classList.add('md:hidden');

    /** @type {HTMLElement|null} */
    const columnHeader = table.querySelector(`th[data-column="${columnId}"]`);
    /** @type {HTMLElement|null} */
    const columnCta = table.querySelector(`[data-column="${columnId}"] .js-membershipFeatureTableColumnCta`);
    if (columnHeader !== null && columnCta !== null) {
        scrollColumnCtaIntoView(columnHeader, columnCta);
    }
}

/**
 * Scroll the newly revealed column's call-to-action into view so members notice the added column.
 * It waits for the column to grow: measured earlier, the column is too narrow and the table snaps past it.
 * @param {HTMLElement} columnHeader
 * @param {HTMLElement} columnCta
 * @returns {Promise<void>}
 */
async function scrollColumnCtaIntoView(columnHeader, columnCta) {
    // A resize cancels the transition, and the scroll still has to run.
    await Promise.allSettled(columnHeader.getAnimations().map((animation) => animation.finished));
    columnCta.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'end',
    });
}

/**
 * Scroll the Professional plan back into view. The nudge hides once it is back, so focus moves to its call-to-action.
 * @param {HTMLElement} clickedButton
 * @returns {void}
 */
function showProfessionalPlan(clickedButton) {
    const tableRoot = clickedButton.closest('.js-membershipFeatureTable');
    tableRoot?.querySelector('th[data-column="professional"]')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'end'});
    tableRoot?.querySelector('[data-column="professional"] .js-membershipFeatureTableColumnCta')?.focus({preventScroll: true});
}

/**
 * Init page, register event handlers.
 * Wires every column toggle on the page (usually the Student / Design League "reveal" button),
 * so each feature-table instance works independently.
 * @returns {void}
 */
function initPage() {
    const toggleButtons = document.querySelectorAll('[data-column-toggle]');
    toggleButtons.forEach((toggleButton) => {
        toggleButton.addEventListener('click', (event) => {
            event.preventDefault();
            displayColumn(toggleButton);
        });
    });

    document.querySelectorAll('.js-membershipFeatureTableShowProfessional').forEach((button) => {
        button.addEventListener('click', () => showProfessionalPlan(button));
    });
}

// init page when the document is ready
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', initPage) : initPage();
